import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR, formatIST } from "@/lib/format";
import { PageHeader } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, ArrowUpFromLine, Lock } from "lucide-react";

export default function UserWithdraw() {
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [method, setMethod] = useState("upi");
  const [f, setF] = useState({ amount: "", upi_id: "", account_number: "", ifsc: "", account_holder: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/user/dashboard").then((r) => setDash(r.data)).catch(() => {});
    api.get("/user/withdrawals").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/user/withdrawals", { ...f, amount: Number(f.amount), payout_method: method });
      toast.success("Withdrawal request submitted. Funds reserved.");
      setF({ amount: "", upi_id: "", account_number: "", ifsc: "", account_holder: "" }); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Withdraw Funds" desc="Request a payout. The amount is reserved until the admin processes it." />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="vp-card rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">New Withdrawal</h3>
            {dash && <span className="text-xs font-mono text-slate-400">Available: <span className="text-emerald-300">{formatINR(dash.available_balance)}</span></span>}
          </div>
          <div className="space-y-4">
            <div><Label className="text-slate-300">Amount (₹)</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="withdraw-amount-input" placeholder="Enter amount" className="mt-1.5 bg-slate-900/60 border-slate-700 h-11" /></div>

            <Tabs value={method} onValueChange={setMethod}>
              <TabsList className="bg-slate-900/60 w-full grid grid-cols-2"><TabsTrigger value="upi" data-testid="method-upi">UPI</TabsTrigger><TabsTrigger value="bank" data-testid="method-bank">Bank</TabsTrigger></TabsList>
              <TabsContent value="upi" className="pt-3">
                <Label className="text-slate-300">Your UPI ID</Label>
                <Input value={f.upi_id} onChange={(e) => setF({ ...f, upi_id: e.target.value })} data-testid="withdraw-upi-input" placeholder="name@bank" className="mt-1.5 bg-slate-900/60 border-slate-700 h-11 font-mono" />
              </TabsContent>
              <TabsContent value="bank" className="pt-3 space-y-3">
                <div><Label className="text-slate-300">Account Number</Label><Input value={f.account_number} onChange={(e) => setF({ ...f, account_number: e.target.value })} data-testid="withdraw-acc-input" className="mt-1.5 bg-slate-900/60 border-slate-700 h-11 font-mono" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-slate-300">IFSC</Label><Input value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value })} data-testid="withdraw-ifsc-input" className="mt-1.5 bg-slate-900/60 border-slate-700 h-11 font-mono" /></div>
                  <div><Label className="text-slate-300">Holder Name</Label><Input value={f.account_holder} onChange={(e) => setF({ ...f, account_holder: e.target.value })} className="mt-1.5 bg-slate-900/60 border-slate-700 h-11" /></div>
                </div>
              </TabsContent>
            </Tabs>

            <Button onClick={submit} disabled={busy || !f.amount} data-testid="submit-withdraw-btn" className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><ArrowUpFromLine className="mr-2 h-5 w-5" />Request Withdrawal</>}
            </Button>
          </div>
        </div>

        <div className="vp-card rounded-xl p-6 flex flex-col justify-center">
          <div className="h-11 w-11 grid place-items-center rounded-xl bg-amber-500/10 text-amber-400 mb-4"><Lock className="h-5 w-5" /></div>
          <h3 className="font-semibold text-lg">How payouts work</h3>
          <ul className="mt-3 space-y-2 text-sm text-slate-400">
            <li>• On request, the amount is instantly <span className="text-amber-300">reserved</span> from your available balance.</li>
            <li>• If the admin <span className="text-rose-300">rejects</span>, the reserved amount is returned.</li>
            <li>• When <span className="text-emerald-300">paid</span>, the amount is deducted once and the admin's UTR appears in your history.</li>
          </ul>
          {dash && <div className="mt-5 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-sm font-mono">Reserved now: <span className="text-amber-300">{formatINR(dash.reserved)}</span></div>}
        </div>
      </div>

      <h3 className="font-semibold text-lg mt-8 mb-3">Your Withdrawals</h3>
      <div className="vp-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="border-white/5 hover:bg-transparent"><TableHead className="text-slate-400">Txn ID</TableHead><TableHead className="text-slate-400">Amount</TableHead><TableHead className="text-slate-400">Payout</TableHead><TableHead className="text-slate-400">Admin UTR</TableHead><TableHead className="text-slate-400">Date (IST)</TableHead><TableHead className="text-slate-400">Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">No withdrawals yet.</TableCell></TableRow> :
                rows.map((t) => (
                  <TableRow key={t.id} className="border-white/5" data-testid={`my-withdrawal-${t.txn_id}`}>
                    <TableCell className="font-mono text-xs text-slate-400">{t.txn_id}</TableCell>
                    <TableCell className="font-mono text-rose-300">{formatINR(t.amount)}</TableCell>
                    <TableCell className="text-xs text-slate-400 max-w-[160px] truncate" title={t.payout_details}>{t.payout_details || "—"}</TableCell>
                    <TableCell className="font-mono text-xs text-emerald-300">{t.utr || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{formatIST(t.created_at)}</TableCell>
                    <TableCell><StatusBadge status={t.status} />{t.status === "rejected" && t.remark ? <div className="text-xs text-rose-300 mt-1">{t.remark}</div> : null}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
